import { useEffect, useState } from "react";
import { Bookmark, History as HistoryIcon, MessageSquareText, Smile, HeartHandshake, MessagesSquare, ScrollText, MessageCircleReply, Swords } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const TOOL_META = {
  text: { label: "Slang decode", icon: MessageSquareText },
  emoji: { label: "Emoji decode", icon: Smile },
  behavior: { label: "Behaviour", icon: HeartHandshake },
  coach_starters: { label: "Conversation starters", icon: MessagesSquare },
  coach_script: { label: "Script builder", icon: ScrollText },
  coach_reply: { label: "Reply coach", icon: MessageCircleReply },
  coach_repair: { label: "Apology & repair", icon: HeartHandshake },
  simulator: { label: "Conflict simulator", icon: Swords },
};

function title(item) {
  const r = item.result || {};
  if (r.term) return r.term;
  if (r.scenario) return r.scenario;
  if (item.tool?.startsWith("coach_")) return (TOOL_META[item.tool]?.label) || "Coach";
  return item.input || "Saved item";
}
function subtitle(item) {
  const r = item.result || {};
  return r.meaning || r.reply || r.opening || r.acknowledge || r.summary || (r.items?.[0]?.opener) || (r.report?.summary) || "";
}

export default function History() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = () => { setLoading(true); api.get(`/history?saved=${filter === "saved"}`).then(({ data }) => setItems(data.items)).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const toggleSave = async (id) => { try { const { data } = await api.post(`/history/${id}/save`); toast.success(data.saved ? "Saved" : "Removed"); load(); } catch { toast.error("Couldn't update"); } };

  return (
    <div className="space-y-6" data-testid="history-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <HistoryIcon size={22} style={{ color: "var(--accent)" }} />
          <h1 className="font-serif text-[28px] sm:text-[34px] font-semibold">Your deck</h1>
        </div>
        <div className="flex gap-2">
          <button className={`btn ${filter === "all" ? "btn-secondary" : "btn-outline"}`} onClick={() => setFilter("all")} data-testid="history-filter-all">All history</button>
          <button className={`btn ${filter === "saved" ? "btn-secondary" : "btn-outline"}`} onClick={() => setFilter("saved")} data-testid="history-filter-saved">Saved only</button>
        </div>
      </div>

      {loading ? (
        <p className="text-sm" style={{ color: "var(--text-muted)" }}>Loading…</p>
      ) : items.length === 0 ? (
        <div className="dk-card p-8 text-center space-y-2">
          <p className="font-serif text-[18px]">Nothing here yet</p>
          <p className="text-sm" style={{ color: "var(--text-muted)" }}>Your decodes and coaching will appear here — everything encrypted and private.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {items.map((it) => {
            const meta = TOOL_META[it.tool] || { label: it.tool, icon: HistoryIcon };
            const Icon = meta.icon;
            return (
              <div key={it.id} className="dk-card p-5 flex items-start gap-4" data-testid="history-item">
                <span className="grid place-items-center w-10 h-10 rounded-xl shrink-0" style={{ background: "var(--sage-tint)" }}><Icon size={18} style={{ color: "var(--sage)" }} /></span>
                <div className="flex-1 min-w-0">
                  <span className="badge-soft">{meta.label}</span>
                  <p className="font-serif text-[17px] mt-1 truncate">{title(it)}</p>
                  <p className="text-sm line-clamp-2" style={{ color: "var(--text-muted)" }}>{subtitle(it)}</p>
                </div>
                <button className={`btn btn-ghost px-2 ${it.saved ? "text-[#B04A39]" : ""}`} onClick={() => toggleSave(it.id)} data-testid={`history-save-${it.id}`} aria-label="Save">
                  <Bookmark size={18} fill={it.saved ? "currentColor" : "none"} />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
